import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, mergeMap, Observable, of } from 'rxjs';
import { MenuModel } from '@models/menu/menu.model';
import { CompanyService } from '@services/company.service';
import { MealPlanService } from '@services/meal-plan.service';
import { MealmomentService } from '@services/meal-moment.service';
import { MenuService } from '@services/menu.service';
import { AppRoutes } from '@helpers/app-routes';

export const orderMenuResolver: ResolveFn<MenuModel> = (): Observable<
  MenuModel | RedirectCommand
> => {
  const router = inject(Router);
  const companyService = inject(CompanyService);
  const menuService = inject(MenuService);
  const mealPlanService = inject(MealPlanService);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);
  const mealPlan = mealPlanService.currentMealPlan();
  const currentMealMomentId =
    mealPlan?.meal_moment.qdm_id ?? inject(MealmomentService).currentMealMoment()?.qdm_id;

  const cancelWithError = (messageKey: string, titleKey: string): Observable<RedirectCommand> => {
    mealPlanService.clearMealPlan();
    toastr.error(translate.instant(messageKey), translate.instant(titleKey));
    return router.navigated
      ? EMPTY
      : of(new RedirectCommand(router.parseUrl(`/${AppRoutes.chooseOrderType}`)));
  };

  return companyService.getCompany().pipe(
    mergeMap((company) => menuService.getMenus(company!.id)),
    mergeMap((menus) => {
      const menu = menus.find((menu) => menu.mealtime_id === currentMealMomentId);
      const serviceIds = mealPlan?.admission_parts.map((part) => part.service_id);
      const hasCourses = (menu?.components ?? []).some(
        (component) =>
          component.courses.length > 0 && (!serviceIds || serviceIds.includes(component.family_id)),
      );

      if (!menu || !hasCourses) {
        return cancelWithError('order.errors.no_menu_message', 'order.errors.no_menu_title');
      }

      return of(menu);
    }),
    catchError(() =>
      cancelWithError('order.errors.load_error_message', 'order.errors.load_error_title'),
    ),
  );
};
